import React, { FC } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { Typography, makeStyles } from '@material-ui/core';
import { apm } from '@elastic/apm-rum';
import { ErrorBoundary as ErrorBoundaryComponent, FallbackProps } from 'react-error-boundary';
import { GatsbyImage, getImage, ImageDataLike } from 'gatsby-plugin-image';
import { ButtonFooter } from 'common/components';
import SmileyFace from '../../icons/SmileyFace';

const styles = makeStyles(({ palette, spacing }) => ({
  icon: {
    height: spacing(20.25),
    width: spacing(20.25),
  },
  errorMessage: {
    backgroundColor: palette.common.white,
    borderRadius: spacing(2),
    padding: spacing(8),
    zIndex: 1,
    width: '100%',
  },
  title: {
    marginBottom: spacing(10),
    textAlign: 'center',
  },
  subTitleText: {
    marginTop: spacing(10),
    textAlign: 'center',
  },
  subTitleDescText: {
    marginTop: spacing(4),
    textAlign: 'center',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  informationContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '80%',
    margin: `0px ${spacing(5)}px`,
  },
  footer: {
    left: '0px',
    right: '0px',
    bottom: '0px',
    position: 'fixed',
    padding: `${spacing(4.5)}px ${spacing(5)}px ${spacing(7.5)}px ${spacing(5)}px`,
    backgroundColor: palette.background.default,
    boxShadow: `0px -1px 5px ${(palette.background as any).contrastShadow}`,
    borderRadius: '10px 5px 0px 0px',
  },
}));

const ErrorPage = ({ error }: FallbackProps) => {
  const data = useStaticQuery<GatsbyTypes.ErrorBoundaryQueryQuery>(graphql`
    query ErrorBoundaryQuery {
      errorList: markdownRemark(frontmatter: { path: { eq: "errors" } }) {
        frontmatter {
          component {
            background {
              childImageSharp {
                ...HeroImage
              }
            }
            title
            description
            message
            retryButton
          }
        }
      }
    }
  `);

  const errorPageCopy = data.errorList!.frontmatter!.component!;

  const { background, title, description, message, retryButton } = errorPageCopy;

  const reload = () => window.location.reload();
  const image = getImage(background as ImageDataLike);
  const classes = styles();

  apm.captureError(error);

  return (
    <>
      {image && (
        <GatsbyImage
          image={image}
          alt=""
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            minHeight: '100vh',
            left: 0,
          }}
        />
      )}
      <div className={classes.informationContainer}>
        <div className={classes.errorMessage}>
          <Typography className={classes.title} variant="h1">
            {title}
          </Typography>
          <div className={classes.iconContainer}>
            <SmileyFace className={classes.icon} />
          </div>
          <Typography className={classes.subTitleText} variant="h2" component="h1">
            {description}
          </Typography>
          <Typography className={classes.subTitleDescText} variant="body1" component="h1">
            {message}
          </Typography>
        </div>
      </div>
      <ButtonFooter buttonText={retryButton} onClick={reload} />
    </>
  );
};

const ErrorBoundary: FC = ({ children }) => (
  <ErrorBoundaryComponent FallbackComponent={ErrorPage}>{children}</ErrorBoundaryComponent>
);

export default ErrorBoundary;
